import React from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2 } from 'lucide-react';
import { INSPECTION_TYPES, INSPECTION_STATUTS } from '../../constants/inspectionTypes';

/**
 * InspectionFormSheet - Sprint 2
 * Sheet de creation/edition d'une inspection CSPS.
 */
export function InspectionFormSheet({
  open, onOpenChange, formData, setFormData, chantiers,
  onSubmit, submitting, isEditing,
}) {
  const handleEntreprisesChange = (text) => {
    const arr = text.split(',').map(s => s.trim()).filter(Boolean);
    setFormData({ ...formData, entreprises_concernees: arr });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="inspection-form-sheet">
        <SheetHeader>
          <SheetTitle data-testid="inspection-form-title">
            {isEditing ? 'Modifier l\'inspection' : 'Nouvelle inspection'}
          </SheetTitle>
          <SheetDescription>
            Renseignez les informations principales. Les notes vocales et photos s'ajoutent ensuite depuis la fiche detail.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="mt-6 space-y-5">
          {/* Chantier */}
          <div>
            <Label htmlFor="insp-chantier">Chantier *</Label>
            <Select value={formData.chantier_id || ''} onValueChange={(v) => setFormData({ ...formData, chantier_id: v })}>
              <SelectTrigger id="insp-chantier" className="h-12 mt-1" data-testid="insp-chantier-select">
                <SelectValue placeholder="Choisir un chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {chantiers.map(c => (
                  <SelectItem key={c.id} value={c.id} className="py-3"
                    data-testid={`insp-chantier-item-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type */}
          <div>
            <Label htmlFor="insp-type">Type d'inspection *</Label>
            <Select value={formData.type || ''} onValueChange={(v) => setFormData({ ...formData, type: v })}>
              <SelectTrigger id="insp-type" className="h-12 mt-1" data-testid="insp-type-select">
                <SelectValue placeholder="Choisir un type" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {INSPECTION_TYPES.map(t => (
                  <SelectItem key={t.key} value={t.key} className="py-3"
                    data-testid={`insp-type-item-${t.key}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="insp-date">Date *</Label>
              <Input id="insp-date" type="date"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="h-12 mt-1" data-testid="insp-date" required />
            </div>
            <div>
              <Label htmlFor="insp-statut">Statut</Label>
              <Select value={formData.statut || 'en_cours'} onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                <SelectTrigger id="insp-statut" className="h-12 mt-1" data-testid="insp-statut-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  {INSPECTION_STATUTS.map(s => (
                    <SelectItem key={s.key} value={s.key} className="py-3"
                      data-testid={`insp-statut-item-${s.key}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Titre */}
          <div>
            <Label htmlFor="insp-titre">Titre / Intitule</Label>
            <Input id="insp-titre"
              value={formData.titre || ''}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              placeholder="Ex: Inspection mensuelle Fevrier"
              className="h-12 mt-1" data-testid="insp-titre" />
          </div>

          {/* Coordonnateur */}
          <div>
            <Label htmlFor="insp-coord">Coordonnateur SPS</Label>
            <Input id="insp-coord"
              value={formData.coordonnateur || ''}
              onChange={(e) => setFormData({ ...formData, coordonnateur: e.target.value })}
              placeholder="Nom du coordonnateur"
              className="h-12 mt-1" data-testid="insp-coord" />
          </div>

          {/* Entreprises */}
          <div>
            <Label htmlFor="insp-entr">Entreprises concernees</Label>
            <Input id="insp-entr"
              value={(formData.entreprises_concernees || []).join(', ')}
              onChange={(e) => handleEntreprisesChange(e.target.value)}
              placeholder="Separer par des virgules : BAT SARL, ELEC SA"
              className="h-12 mt-1" data-testid="insp-entreprises" />
          </div>

          {/* Notes texte */}
          <div>
            <Label htmlFor="insp-notes">Notes (compte-rendu rapide)</Label>
            <Textarea id="insp-notes"
              value={formData.notes_texte || ''}
              onChange={(e) => setFormData({ ...formData, notes_texte: e.target.value })}
              rows={4}
              placeholder="Observations directes pendant l'inspection..."
              className="mt-1" data-testid="insp-notes" />
          </div>

          {/* Synthese */}
          <div>
            <Label htmlFor="insp-synthese">Synthese</Label>
            <Textarea id="insp-synthese"
              value={formData.synthese || ''}
              onChange={(e) => setFormData({ ...formData, synthese: e.target.value })}
              rows={3}
              placeholder="Bilan global de l'inspection..."
              className="mt-1" data-testid="insp-synthese" />
          </div>

          {/* Observations */}
          <div>
            <Label htmlFor="insp-observations">Observations / Points de vigilance</Label>
            <Textarea id="insp-observations"
              value={formData.observations || ''}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              rows={3}
              placeholder="Non-conformites, actions requises, recommandations..."
              className="mt-1" data-testid="insp-observations" />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              data-testid="insp-form-cancel">
              Annuler
            </Button>
            <Button type="submit" disabled={submitting}
              className="bg-[#FF6B00] hover:bg-orange-600"
              data-testid="insp-form-submit">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Enregistrer' : 'Creer l\'inspection'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
